import { useState } from "react";
import { Loader2, RotateCcw, Trash2, UserX } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

const COPY = {
  remove: {
    title: (n) => `Remove ${n} from the club?`,
    body: "Their profile, photos and messages disappear for everyone, they are signed out and can no longer sign in. Experiences they lead are cancelled and the circle is told. Everything is kept for 30 days so you can restore them, then deleted for good.",
    button: "Remove member",
  },
  delete: {
    title: (n) => `Delete ${n} permanently?`,
    body: "This wipes their account, photos, videos, messages, connections and experiences right now. It cannot be undone.",
    button: "Delete permanently",
  },
};

const ConfirmDialog = ({ a, mode, onClose, onDone }) => {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const c = COPY[mode];
  const match = typed.trim().toLowerCase() === a.email.toLowerCase();
  const run = async () => {
    setBusy(true);
    try {
      if (mode === "remove") {
        const { data } = await api.post(`/admin/users/${a.id}/remove`);
        toast.success(`${a.name} removed${data.cancelled_experiences ? ` · ${data.cancelled_experiences} experience(s) cancelled` : ""}`);
      } else {
        await api.delete(`/admin/users/${a.id}`, { data: { confirm: typed.trim() } });
        toast.success(`${a.name} deleted permanently`);
      }
      onDone(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md border-destructive/40 bg-card" data-testid={`admin-${mode}-dialog-${a.id}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-destructive">{c.title(a.name.split(" ")[0])}</DialogTitle>
          <DialogDescription>{c.body}</DialogDescription>
        </DialogHeader>
        <label className="field-label" htmlFor={`confirm-${mode}-${a.id}`}>Type <span className="select-all font-mono text-foreground">{a.email}</span> to confirm</label>
        <input id={`confirm-${mode}-${a.id}`} className="field" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={a.email} data-testid={`admin-${mode}-confirm-input`} />
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-outline" onClick={onClose} data-testid={`admin-${mode}-cancel`}>Cancel</button>
          <button type="button" className="btn-primary bg-destructive text-destructive-foreground hover:bg-destructive/90" disabled={!match || busy} onClick={run} data-testid={`admin-${mode}-submit`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} {c.button}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const RemoveMemberTools = ({ a, onChange }) => {
  const { user } = useAuth();
  const [mode, setMode] = useState("");
  const [busy, setBusy] = useState(false);
  if (a.role === "admin" || a.id === user?.id) return null;
  const restore = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/users/${a.id}/restore`); toast.success(`${a.name} restored (${data.status})`); onChange(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const danger = "btn-outline h-9 px-3 text-xs border-destructive/50 text-destructive hover:border-destructive hover:text-destructive";
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4" data-testid={`remove-member-tools-${a.id}`}>
      {a.status === "removed" ? (
        <>
          <p className="mr-auto text-sm text-muted-foreground" data-testid={`removed-info-${a.id}`}>
            Removed {a.removed_at && new Date(a.removed_at).toLocaleDateString("en-GB")}{a.purge_after && ` · deleted for good on ${new Date(a.purge_after).toLocaleDateString("en-GB")}`}
          </p>
          <button type="button" className="btn-outline h-9 px-3 text-xs" disabled={busy} onClick={restore} data-testid={`admin-restore-${a.id}`}><RotateCcw className="h-3.5 w-3.5" /> Restore</button>
          <button type="button" className={danger} onClick={() => setMode("delete")} data-testid={`admin-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /> Delete permanently</button>
        </>
      ) : (
        <button type="button" className={`${danger} ml-auto`} onClick={() => setMode("remove")} data-testid={`admin-remove-${a.id}`}><UserX className="h-3.5 w-3.5" /> Remove member</button>
      )}
      {mode && <ConfirmDialog a={a} mode={mode} onClose={() => setMode("")} onDone={onChange} />}
    </div>
  );
};
